import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { AxiosWrapper } from 'services/AxiosWrapper';
import { BookingManagerApiService } from 'services/BookingManagerApi';
import { ItineraryManagerApiService } from 'services/ItineraryManagerApi';
import { useNotifications } from 'hooks/useNotifications';

export const useItineraryBreakdownQueries = (
  bookingManagerApi: BookingManagerApiService<AxiosWrapper<AxiosInstance>>,
  itineraryManagerApi: ItineraryManagerApiService<AxiosWrapper<AxiosInstance>>,
  bookingUuid: string | undefined
) => {
  const queryClient = useQueryClient();
  const notificationOps = useNotifications();

  return {
    invalidateGetItineraryVersionList: () =>
      queryClient.invalidateQueries({ queryKey: ['getItineraryVersionList', bookingUuid] }),
    postGenerateItinerary: useMutation({
      mutationFn: async (bookingUuid: string) => {
        return bookingManagerApi.postGenerateItinerary(bookingUuid);
      },
      onSuccess: () => {
        notificationOps.showSuccessNotification('Itinerary generated successfully');
        queryClient.invalidateQueries({ queryKey: ['getItineraryVersionList', bookingUuid] });

        const url = window.location.origin + `/itinerary/${bookingUuid}/1`;
        const newWindow = window.open(url, '_blank');

        // Check if popup was blocked
        if (newWindow === null || typeof newWindow === 'undefined') {
          console.warn('Failed to open new tab. Popup might be blocked by the browser.');
          return false;
        }
        newWindow.focus();
      },
      onError: () => {
        notificationOps.showErrorNotification('Failed to generate itinerary');
      },
    }),
    getItineraryVersionList: useQuery({
      queryKey: ['getItineraryVersionList', bookingUuid],
      queryFn: async () => {
        if (!bookingUuid) {
          return;
        }
        return itineraryManagerApi.getItineraryVersionList(bookingUuid);
      },
    }),
  };
};
